import { CompanyIcon, ReCalculateIcon } from 'assets/icons';
import classNames from 'classnames';
import { CustomButton, CustomInput, CustomModal } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import React, { useState } from 'react';
import { CircleCloseIcon } from 'assets/icons';
import { CommissionData } from 'containers/jobs/jobs.model';
import { useRecalculateJobCommissionSplitMutation, useUpdateJobCommissionSplitMutation } from 'containers/jobs/api';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, showNotifier } from 'containers/app/appSlice';
import { getCommissionBaseLabel, isValidDecimalInput } from 'containers/settings/utils';
import { cleanNumberInput } from 'containers/settings/utils';
import { NotifierTypes } from 'constants/notifierConstants';
import { getAmountWithCurrency } from 'utils/amountUtils';
interface CommissionDataWithEditingField extends CommissionData {
  editingField?: 'splitPercentage' | 'commission' | null;
  isEdited?: boolean;
}

interface EditCommissionsModalProps {
  onClose: () => void;
  onSave: () => void;
  initialCommissionData: CommissionData[];
  jobNumber?: string;
}

const EditCommissionsModal: React.FC<EditCommissionsModalProps> = ({
  onClose,
  onSave,
  initialCommissionData,
  jobNumber
}) => {
  const dispatch = useAppDispatch();
  const team = useAppSelector(selectCurrentTeam);

  const [commissionData, setCommissionData] = useState<CommissionDataWithEditingField[]>(
    initialCommissionData.map(item => ({ ...item, editingField: null, isEdited: false }))
  );

  const [recalculateJobCommissionSplit, { isLoading: isRecalculating }] = useRecalculateJobCommissionSplitMutation();
  const [updateJobCommissionSplit, { isLoading: isUpdating }] = useUpdateJobCommissionSplitMutation();

  const handleSplitPercentageChange = (value: string, index: number) => {
    let cleanedValue = cleanNumberInput(value);

    if (cleanedValue.length > 1 && cleanedValue.startsWith('0') && cleanedValue.charAt(1) !== '.') {
      cleanedValue = cleanedValue.substring(1);
    }

    if (isValidDecimalInput(cleanedValue)) {
      const newCommissionData = [...commissionData];
      newCommissionData[index].splitPercentage = cleanedValue ? cleanedValue : '0';
      newCommissionData[index].editingField = 'splitPercentage';
      newCommissionData[index].isEdited = true;
      setCommissionData(newCommissionData);
    }
  };

  const handleCommissionChange = (value: string, index: number) => {
    const cleanedValue = cleanNumberInput(value);

    if (isValidDecimalInput(cleanedValue)) {
      const newCommissionData = [...commissionData];
      newCommissionData[index] = {
        ...newCommissionData[index],
        commissionAmount: cleanedValue,
        splitPercentage: null,
        rate: null,
        base: null,
        baseAmount: null,
        ruleTitle: null,
        editingField: 'commission',
        executionType: 'MANUAL',
        isEdited: true
      };
      setCommissionData(newCommissionData);
    }
  };

  const handleSave = () => {
    if (commissionData.some(item => item.editingField === 'splitPercentage')) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Please recalculate first to save the changes'
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
      return;
    }

    updateJobCommissionSplit({
      teamId: team.id,
      jobId: commissionData[0].jobId,
      body: {
        data: commissionData.map(item => ({
          merchantId: item.merchant.id,
          executionType: item.editingField === 'commission' ? 'MANUAL' : item.executionType || 'AUTO',
          commissionAmount: parseFloat(item.commissionAmount),
          base: item.base,
          baseAmount: item.baseAmount,
          splitPercentage: item.splitPercentage,
          ruleId: item.ruleId,
          isEdited: item.isEdited
        }))
      },
      commissionData
    })
      .unwrap()
      .then(() => {
        onSave();
      });
  };

  const handleRecalculateCommission = (item: CommissionDataWithEditingField, index: number) => {
    recalculateJobCommissionSplit({
      teamId: item.teamId,
      body: {
        jobId: item.jobId,
        merchantId: item.merchant.id,
        splitPercentage: item.splitPercentage ? parseFloat(item.splitPercentage) : 0,
        defaultSplit: item.editingField !== 'splitPercentage'
      }
    })
      .unwrap()
      .then(data => {
        setCommissionData(prev => {
          const newCommissionData = [...prev];
          newCommissionData[index].splitPercentage = data.splitPercentage;
          newCommissionData[index].base = data.base;
          newCommissionData[index].baseAmount = data.baseAmount;
          newCommissionData[index].rate = data.rate;
          newCommissionData[index].commissionAmount = data.commissionAmount;
          newCommissionData[index].ruleId = data.ruleId;
          newCommissionData[index].editingField = null;
          newCommissionData[index].executionType = 'AUTO';
          newCommissionData[index].ruleTitle = data.ruleTitle;
          return newCommissionData;
        });
      });
  };

  return (
    <CustomModal open={true} width="md:w-[900px] lg:w-[1000px] xl:w-[1200px]">
      <div className="flex flex-col p-10">
        <div className="flex items-center justify-between">
          <div className="text-headingGray">
            <div className="text-xl font-bold">Edit commissions</div>
            {jobNumber && <span className="mt-2 text-sbase font-semibold">{jobNumber}</span>}
          </div>
          <CircleCloseIcon onClick={onClose} className="shrink-0 cursor-pointer" />
        </div>

        <div className="mt-6 w-full overflow-hidden rounded-lg border shadow-sm">
          <table className="w-full border-collapse bg-white">
            <thead className="border-b-2 bg-gray-50 text-sbase font-semibold text-[#878787]">
              <tr>
                <th className="py-2 px-4 text-left">Name</th>
                <th className="py-2 px-4 text-left">Split %</th>
                <th className="py-2 px-4 text-left">Rule</th>
                <th className="py-2 px-4 text-left">Base</th>
                <th className="py-2 px-4 text-left">Rate</th>
                <th className="py-2 px-4 text-left">Commission</th>
                <th className="py-2 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {commissionData.map((item, index) => (
                <tr key={item.merchant.id}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 overflow-hidden rounded-full bg-gray-200">
                        {item.merchant.profilePictureURL ? (
                          <img
                            src={item.merchant.profilePictureURL}
                            alt={item.merchant.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-secondary text-primary">
                            {item.merchant.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <span className="text-sbase font-bold text-primaryText">{item.merchant.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                    <CustomInput
                      id={`split-percentage-${index}`}
                      value={item.splitPercentage ?? '-'}
                      onChange={value => handleSplitPercentageChange(value, index)}
                      inputStyle="min-w-[52px] max-w-[80px]"
                      className="h-auto py-1 pl-2.5 pr-6"
                      suffix="%"
                      showSuffixAlways
                      suffixStyle="text-accentText top-1"
                    />
                  </td>
                  <td
                    className={classNames('px-4 py-2 text-sbase font-bold text-primaryText', {
                      'text-accentText italic': item.editingField === 'splitPercentage'
                    })}>
                    {item.executionType === 'MANUAL' ? (
                      <span
                        className={classNames(
                          'flex items-center gap-1 w-fit rounded-md border border-gray-1 px-2 py-1 font-medium',
                          'text-accentText'
                        )}>
                        <CompanyIcon className="path-fill-current h-[13px] w-[13px]" />
                        Manual
                      </span>
                    ) : (
                      item.ruleTitle || '-'
                    )}
                  </td>
                  <td
                    className={classNames('px-4 py-2 text-sbase font-bold text-primaryText', {
                      'text-accentText italic': item.editingField === 'splitPercentage'
                    })}>
                    <span className="flex flex-col gap-1">
                      <span>{item?.baseAmount ? `${getAmountWithCurrency(item?.baseAmount)}` : '-'}</span>
                      <span className="text-13px font-medium text-accentText">{getCommissionBaseLabel(item.base)}</span>
                    </span>
                  </td>
                  <td
                    className={classNames('px-4 py-2 text-sbase font-bold text-primaryText', {
                      'text-accentText italic': item.editingField === 'splitPercentage'
                    })}>
                    {item.rate && item.rate !== '-'
                      ? `${
                          parseFloat(parseFloat(item.rate).toFixed(2)) === parseInt(item.rate)
                            ? parseInt(item.rate)
                            : parseFloat(item.rate).toFixed(2)
                        }%`
                      : '-'}
                  </td>
                  <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                    <CustomInput
                      id={`commission-${index}`}
                      value={item.commissionAmount}
                      onChange={value => handleCommissionChange(value, index)}
                      className={classNames('h-auto py-1 pr-2.5 pl-6 max-w-[100px]', {
                        'hover:cursor-not-allowed text-accentText italic': item.editingField === 'splitPercentage'
                      })}
                      prefix="$"
                      prefixWrapperStyle="text-accentText left-2.5"
                      disabled={item.editingField === 'splitPercentage'}
                    />
                  </td>
                  <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                    <CustomButton
                      id="recalculate-commission-button"
                      type={ButtonType.SECONDARY}
                      childrenContainerStyles="text-17px"
                      StartIcon={ReCalculateIcon}
                      disabled={item.editingField !== 'splitPercentage' || isRecalculating}
                      onClick={() => handleRecalculateCommission(item, index)}>
                      Recalculate
                    </CustomButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex items-center justify-end gap-4">
          <CustomButton
            id="cancel-commission-button"
            type={ButtonType.SECONDARY}
            childrenContainerStyles="text-17px"
            onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            id="save-commission-button"
            type={ButtonType.PRIMARY}
            childrenContainerStyles="text-17px"
            onClick={handleSave}
            disabled={isUpdating || isRecalculating}>
            Save
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default EditCommissionsModal;
